import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import AdminStats from './AdminStats';
import FeedbackAdmin from './FeedbackAdmin';

const PLACEMENTS = [
  { value: 'overview', label: 'Overview (featured)' },
  { value: 'transactions', label: 'Transactions (banks/cards)' },
  { value: 'subscriptions', label: 'Subscriptions (streaming/services)' },
  { value: 'magi', label: 'MAGI (health insurance)' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'investments', label: 'Investments (brokerages/advisors)' },
];

export default function AdminPanel() {
  const [sponsors, setSponsors] = useState(null);
  const [slotLimits, setSlotLimits] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', tagline: '', logoUrl: '', placement: 'overview', linkUrl: '', displayOrder: 0 });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sponsorData, limitsData] = await Promise.all([
        api.getAllSponsorsAdmin(),
        api.getSponsorSlotLimits(),
      ]);
      setSponsors(sponsorData);
      setSlotLimits(limitsData);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ name: '', tagline: '', logoUrl: '', placement: 'overview', linkUrl: '', displayOrder: 0 });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const startEdit = (s) => {
    setForm({
      name: s.name,
      tagline: s.tagline || '',
      logoUrl: s.logo_url || '',
      placement: s.placement,
      linkUrl: s.link_url || '',
      displayOrder: s.display_order || 0,
    });
    setEditingId(s.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        tagline: form.tagline.trim() || undefined,
        logoUrl: form.logoUrl.trim() || undefined,
        placement: form.placement,
        linkUrl: form.linkUrl.trim() || undefined,
        displayOrder: parseInt(form.displayOrder) || 0,
      };
      if (editingId) {
        await api.updateSponsor(editingId, payload);
      } else {
        await api.createSponsor(payload);
      }
      resetForm();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (s) => {
    setError('');
    try {
      await api.updateSponsor(s.id, { isActive: !s.is_active });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this sponsor permanently?')) return;
    await api.deleteSponsor(id);
    load();
  };

  const updateLimit = async (placement, maxSlots) => {
    try {
      await api.updateSponsorSlotLimit(placement, maxSlots);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  if (!sponsors) return <div style={styles.panel}><p style={styles.empty}>Loading...</p></div>;

  const grouped = PLACEMENTS.map((p) => ({
    ...p,
    items: sponsors.filter((s) => s.placement === p.value),
  }));

  return (
    <div style={styles.grid}>
      <AdminStats />
      <FeedbackAdmin />
      <div style={styles.panel}>
        <div style={styles.header}>
          <h2 style={styles.title}>🛠 Sponsor Management</h2>
          <button style={styles.addBtn} onClick={() => { resetForm(); setShowForm((s) => !s); }}>
            {showForm ? 'Cancel' : '+ Add Sponsor'}
          </button>
        </div>

        {error && !showForm && <p style={styles.generalError}>{error}</p>}

        {showForm && (
          <form style={styles.form} onSubmit={submit}>
            <input
              style={styles.input}
              placeholder="Sponsor name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              style={styles.input}
              placeholder="Tagline (optional)"
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
            />
            <input
              style={styles.input}
              placeholder="Logo URL (optional - a link to their logo image)"
              value={form.logoUrl}
              onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            />
            <select
              style={styles.input}
              value={form.placement}
              onChange={(e) => setForm({ ...form, placement: e.target.value })}
            >
              {PLACEMENTS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <input
              style={styles.input}
              placeholder="Link URL (optional)"
              value={form.linkUrl}
              onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
            />
            <input
              style={styles.input}
              type="number"
              placeholder="Display order (lower shows first)"
              value={form.displayOrder}
              onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
            />
            {error && <p style={styles.error}>{error}</p>}
            <button style={styles.submitBtn} type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : editingId ? 'Save Changes' : 'Add Sponsor'}
            </button>
          </form>
        )}

        {grouped.map((group) => (
          <div key={group.value} style={styles.group}>
            <div style={styles.groupLabel}>{group.label}</div>
            {group.items.length === 0 ? (
              <p style={styles.emptySmall}>No sponsors in this slot.</p>
            ) : (
              group.items.map((s) => (
                <div key={s.id} style={styles.row}>
                  <div style={styles.rowLeft}>
                    <span style={{
                      ...styles.statusDot,
                      background: s.is_active ? '#00d4aa' : '#4b5563',
                    }} />
                    {s.logo_url && (
                      <span style={styles.logoThumb}>
                        <img src={s.logo_url} alt="" style={styles.logoThumbImg} onError={(e) => { e.target.style.display = 'none'; }} />
                      </span>
                    )}
                    <div>
                      <div style={styles.sponsorName}>{s.name}</div>
                      {s.tagline && <div style={styles.sponsorTagline}>{s.tagline}</div>}
                    </div>
                  </div>
                  <div style={styles.rowRight}>
                    <button style={styles.smallBtn} onClick={() => toggleActive(s)}>
                      {s.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button style={styles.smallBtn} onClick={() => startEdit(s)}>Edit</button>
                    <button style={styles.smallBtnDanger} onClick={() => remove(s.id)}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        ))}
      </div>

      {slotLimits && (
        <div style={styles.panel}>
          <h2 style={styles.title}>🎟 Slot Limits</h2>
          <p style={styles.limitsHelp}>
            Max sponsors allowed per placement. Trying to enable a sponsor
            beyond this limit will be blocked until you raise it or disable
            another one.
          </p>
          {slotLimits.map((l) => (
            <div key={l.placement} style={styles.limitRow}>
              <span style={styles.limitLabel}>
                {PLACEMENTS.find((p) => p.value === l.placement)?.label || l.placement}
              </span>
              <div style={styles.limitControls}>
                <span style={{
                  ...styles.limitCount,
                  color: l.active_count >= l.max_slots ? '#ef4444' : '#00d4aa',
                }}>
                  {l.active_count}/{l.max_slots} filled
                </span>
                <input
                  type="number"
                  min="0"
                  style={styles.limitInput}
                  defaultValue={l.max_slots}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val !== l.max_slots) updateLimit(l.placement, val);
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  grid: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  addBtn: {
    background: 'transparent',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    padding: '1rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
  },
  input: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '0.5rem 0.6rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
  },
  error: { color: '#ef4444', fontSize: '0.75rem', margin: 0 },
  submitBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '0.6rem',
    fontSize: '0.8rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  group: { marginBottom: '1.25rem' },
  groupLabel: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.5rem',
    fontFamily: "'Syne', sans-serif",
  },
  emptySmall: { color: '#4b5563', fontSize: '0.75rem', margin: 0 },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0.75rem',
    background: '#0a0f1e',
    borderRadius: '8px',
    marginBottom: '0.4rem',
    border: '1px solid #1e2a3a',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  rowLeft: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  sponsorName: {
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontWeight: '500',
    fontFamily: "'Syne', sans-serif",
  },
  sponsorTagline: { color: '#4b5563', fontSize: '0.7rem', marginTop: '2px' },
  rowRight: { display: 'flex', gap: '0.4rem' },
  smallBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.7rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  smallBtnDanger: {
    background: 'transparent',
    border: '1px solid #ef4444',
    color: '#ef4444',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.7rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  empty: { color: '#4b5563', fontSize: '0.875rem', textAlign: 'center', padding: '1rem 0' },
  logoThumb: {
    width: '24px',
    height: '24px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    borderRadius: '5px',
    overflow: 'hidden',
    padding: '2px',
  },
  logoThumbImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  generalError: {
    color: '#ef4444',
    fontSize: '0.8rem',
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    padding: '0.6rem 0.85rem',
    marginBottom: '1rem',
  },
  limitsHelp: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  limitRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0',
    borderBottom: '1px solid #1e2a3a',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  limitLabel: {
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
  },
  limitControls: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  limitCount: {
    fontSize: '0.75rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  limitInput: {
    width: '60px',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '4px 8px',
    color: '#fff',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
  },
};
